import { TrackAssessmentCategorySlug } from '@backrs_ui/common-ui/models/trackAssessment';
import { AppIcon, ICON_SIZES, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { ContentCompetency } from '@backrs_ui/common-ui/src/models/nugget';

import { Box, Theme, SxProps, Typography } from '@mui/material';

const COMPETENCY_ICON_MAP: Record<TrackAssessmentCategorySlug, AppIcons> = {
  'academic-foundations': 'GraduationHat02',
  'real-world-experience': 'Trophy01',
  'mindsets-and-skills': 'Tool02',
  'community-of-success': 'Server06',
  none: 'GraduationHat02',
};

type CompetencyCardProps = Readonly<{
  competency: ContentCompetency;
  onClick?: () => void;
}>;

export const CompetencyCard = ({ competency, onClick }: CompetencyCardProps) => {
  return (
    <Box sx={containerSx(!!onClick)} onClick={onClick}>
      <Box sx={headerContainerSx}>
        <Box sx={badgeIconSx}>
          <AppIcon
            iconName={COMPETENCY_ICON_MAP[competency.unlock.slug]}
            diameter={ICON_SIZES['20px']}
            color="Colors/Foreground/fg-primary"
          />
        </Box>
      </Box>
      <Typography
        variant="text-sm-medium"
        color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
      >
        {competency.unlock.shortname}
      </Typography>
      <Typography
        variant="display-xs-semibold"
        color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
      >
        {competency.title}
      </Typography>
      <Box sx={descriptionContainerSx}>
        <Typography variant="text-sm-regular" sx={descriptionSx}>
          {competency.description}
        </Typography>
      </Box>
    </Box>
  );
};

const containerSx =
  (isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: backrs.spacing['spacing-2xl'],
    minWidth: '308px',
    minHeight: '219px',
    height: '100%',
    width: '100%',
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary-hover']}`,
    borderRadius: backrs.radius['radius-lg'],
    gap: backrs.spacing['spacing-xs'],
    backgroundColor: backrs.colors['Colors/Base/White'],
    ...(isClickable
      ? {
          cursor: 'pointer',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            borderColor: backrs.colors['Colors/Foreground/fg-primary'],
            transform: 'translate(-2px, -2px)',
            boxShadow: backrs.shadows['shadow-lg'],
          },
        }
      : {}),
  });

const descriptionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: backrs.spacing['spacing-xl'],
  flex: 1,
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: backrs.spacing['spacing-2xl'],
});

const badgeIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: backrs.radius['radius-lg'],
  border: `2px solid ${backrs.colors['Gray/800']}`,
  backgroundColor: backrs.colors['Brand/200'],
  width: ICON_SIZES['40px'],
  maxWidth: ICON_SIZES['40px'],
  minWidth: ICON_SIZES['40px'],
  height: ICON_SIZES['40px'],
});

const descriptionSx: SxProps<Theme> = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
